import { z } from "zod";
// Reuse enum admin-trust-moderation (DRY — cùng cột abuse_reports.subjectType/status; xem
// schema.prisma model AbuseReport: subjectType ∈ group|user|fund — "KHÔNG bịa loại ngoài story").
import { abuseReportStatusSchema, abuseSubjectTypeSchema } from "./admin-trust-moderation.js";

/**
 * Shared DTO cho User Abuse-Report ingest (US-DF-7 · @UC9111) — POST /v1/abuse-reports.
 * Một nguồn sự thật shape api ↔ mobile (DRY). Đóng dead-flow DF-7 (audit/DEADFLOW.md): trước đây
 * queue admin `/abuse` LIVE nhưng KHÔNG có đường user TẠO report — bảng `abuse_reports` chỉ seed ghi.
 *
 * INVARIANTS:
 *  - reporterUserId LẤY TỪ access token (server) — KHÔNG nhận từ body (chống IDOR); `.strict()`
 *    chặn field lạ tại biên (C5).
 *  - reason = ENUM category ngắn (server map sang nhãn tiếng Việt khi lưu — cột DB `reason` là text
 *    hiển thị cho reviewer, KHÔNG có cột category riêng). detail là user-text tự do (có thể chứa PII) —
 *    CHỈ lưu vào cột reason hiển thị reviewer, KHÔNG đưa vào audit_log (pii-minimize).
 *  - slaDueAt server tính = createdAt + `trust.abuse_report_sla_hours` (DF-6 wired write-time).
 */

// ============================================================
// Reason category (enum ngắn — user chọn, server map nhãn VN)
// ============================================================

/** Category lý do báo cáo — enum ĐÓNG (user chọn từ danh sách, không tự bịa loại). */
export const abuseReportReasonSchema = z.enum([
  "scam",
  "impersonation",
  "harassment",
  "spam",
  "other",
]);
export type AbuseReportReason = z.infer<typeof abuseReportReasonSchema>;

/**
 * Nhãn tiếng Việt cho từng category — MỘT nguồn sự thật api ↔ mobile ↔ admin (cột `reason` DB lưu
 * text hiển thị reviewer, mirror shape seed: "Nghi lừa đảo gom quỹ" / "Mạo danh thủ quỹ"...).
 */
export const ABUSE_REPORT_REASON_LABELS: Readonly<Record<AbuseReportReason, string>> = {
  scam: "Nghi lừa đảo",
  impersonation: "Mạo danh",
  harassment: "Quấy rối",
  spam: "Spam / quảng cáo",
  other: "Khác",
};

/**
 * Compose chuỗi `reason` lưu DB (cột text duy nhất — schema KHÔNG có cột category/detail riêng):
 * `"<nhãn VN>"` hoặc `"<nhãn VN>: <detail>"`. Export để api (persist) + test + admin (nếu cần parse
 * ngược) dùng CHUNG — tránh drift 2 chỗ format.
 */
export function formatAbuseReportReason(reason: AbuseReportReason, detail?: string): string {
  const label = ABUSE_REPORT_REASON_LABELS[reason];
  const trimmed = detail?.trim();
  return trimmed ? `${label}: ${trimmed}` : label;
}

// ============================================================
// Request / Response — POST /v1/abuse-reports
// ============================================================

/** detail (tự chọn) — mô tả thêm của người báo cáo. 10..1000 để chặn spam rác + input phình to. */
export const ABUSE_REPORT_DETAIL_MIN_LENGTH = 10;
export const ABUSE_REPORT_DETAIL_MAX_LENGTH = 1000;

/**
 * Body POST /v1/abuse-reports: `{ targetType, targetId, reason, detail? }`.
 *  - targetType: group|user|fund (reuse `abuseSubjectTypeSchema` — đúng cột DB subjectType).
 *  - targetId: UUID đối tượng bị báo (server validate tồn tại đúng loại — sai ⇒ 404 đồng nhất).
 *  - reason: category enum (KHÔNG free-text ở biên — server map nhãn VN).
 *  - detail: tự chọn, 10..1000 ký tự (trim) — KHÔNG nhận PII thô nào khác ngoài field này.
 *  - `.strict()` chặn field lạ (reporterUserId/status/slaDueAt từ body — server quyết).
 */
export const createAbuseReportRequestSchema = z
  .object({
    targetType: abuseSubjectTypeSchema,
    targetId: z.string().uuid(),
    reason: abuseReportReasonSchema,
    detail: z
      .string()
      .trim()
      .min(ABUSE_REPORT_DETAIL_MIN_LENGTH)
      .max(ABUSE_REPORT_DETAIL_MAX_LENGTH)
      .optional(),
  })
  .strict();
export type CreateAbuseReportRequest = z.infer<typeof createAbuseReportRequestSchema>;

/**
 * Response tạo report (rút gọn — C2 không lộ nội bộ): `{ code, status, slaDueAt }`.
 *  - code: "#AB-<8 hex>" (generate server, retry P2002).
 *  - status: luôn "open" khi vừa tạo (reuse enum admin).
 *  - slaDueAt: ISO — bằng chứng DF-6 wired (createdAt + `trust.abuse_report_sla_hours`).
 */
export const createAbuseReportResponseSchema = z
  .object({
    code: z.string(),
    status: abuseReportStatusSchema,
    slaDueAt: z.string().datetime(),
  })
  .strict();
export type CreateAbuseReportResponse = z.infer<typeof createAbuseReportResponseSchema>;
